import type { User } from '@/lib/User';
import type { PhotoData, PhotoGeo } from '@/types';
import {
    fullDateFormatter, mediumDateFormatter, monthYearFormatter, shortDateFormatter
} from './utils';

export class Photo {
  private readonly _id: string;
  private readonly _title: string;
  private readonly _description?: string;
  private readonly _imageUrl: string;
  private readonly _geo?: PhotoGeo;
  private readonly _date: Date;
  private readonly _updatedAt: string;
  private readonly _type?: string;
  private readonly _attachedUrl?: string;
  private readonly _thumbUrl?: string;
  private readonly _userId: string;
  private _user?: User;

  constructor(data: PhotoData) {
    this._id = data.id.toString();
    this._title = data.title;
    this._description = data.description;
    this._imageUrl = data.image_url;
    this._date = data.date instanceof Date ? data.date : new Date(data.date);
    this._type = data.type;
    this._attachedUrl = data.attached_url;
    this._thumbUrl = data.thumb_url;
    this._updatedAt = data.updated_at;
    this._userId = data.user_id;

    if (data.geo && data.geo.lat !== undefined && data.geo.lat !== null && data.geo.lng !== undefined && data.geo.lng !== null) {
      this._geo = {
        ...data.geo,
        lat: parseFloat(data.geo.lat.toString()),
        lng: parseFloat(data.geo.lng.toString()),
      };
    }
  }

  // Factory method to create from API data
  static fromApi(data: PhotoData): Photo {
    return new Photo({
      ...data,
      date: new Date(data.date),
    });
  }

  // Getters for all properties
  get id(): string { return this._id; }
  get title(): string { return this._title; }
  get description(): string | undefined { return this._description; }
  get imageUrl(): string { return this._attachedUrl || this._imageUrl; }
  get thumbUrl(): string { return this._thumbUrl || this._imageUrl; }
  get date(): Date { return this._date; }
  get updatedAt(): string { return this._updatedAt; }
  get geo(): PhotoGeo | undefined { return this._geo; }
  get type(): string | undefined { return this._type; }
  get userId(): string { return this._userId; }
  get user(): User | undefined { return this._user; }
  set user(user: User | undefined) { this._user = user; }

  // Type guard for geo data
  hasGeo(): this is Photo & { geo: NonNullable<Photo['geo']> } {
    return this._geo !== undefined && this._geo !== null;
  }

  url(): string {
    return `/photos/${this.id}`;
  }

  // Date-related methods
  shortDate(): string {
    return shortDateFormatter(this._date);
  }

  mediumDate(): string {
    return mediumDateFormatter(this._date);
  }

  fullDate(): string {
    return fullDateFormatter(this._date);
  }

  monthYear(): string {
    return monthYearFormatter(this._date);
  }

  // Location methods
  locationString(): string {
    if (!this.hasGeo()) {
      return '';
    }
    const { name, region, province, state } = this._geo!;

    return [
      name ?? '',
      region ?? '',
      province ?? '',
      state ?? '',
    ].filter(Boolean).join(', ');
  }

  matchesFilter(state?: string, province?: string, region?: string): boolean {
    if (!this.hasGeo()) {
      return false;
    }

    const { state: photoState, province: photoProvince, region: photoRegion } = this._geo!;
    return (
      (!state || photoState === state) &&
      (!province || photoProvince === province) &&
      (!region || photoRegion === region)
    );
  }
}
